/**
 * Utility functions for generating dynamic poster URLs
 */

// TMDB Image Base URL
const TMDB_IMAGE_BASE_URL = 'https://image.tmdb.org/t/p';

/**
 * Generate poster URL with different sizes
 * @param {string} posterPath - The poster path from TMDB API
 * @param {string} size - Image size (w92, w154, w185, w342, w500, w780, original)
 * @returns {string} Complete poster URL or fallback
 */
export const getPosterUrl = (posterPath, size = 'w500') => {
  if (!posterPath) {
    return '/static/media/placeholder-poster.jpg'; // Fallback image
  }
  return `${TMDB_IMAGE_BASE_URL}/${size}${posterPath}`;
};

/**
 * Generate backdrop URL with different sizes
 * @param {string} backdropPath - The backdrop path from TMDB API
 * @param {string} size - Image size (w300, w780, w1280, original)
 * @returns {string} Complete backdrop URL or fallback
 */
export const getBackdropUrl = (backdropPath, size = 'w1280') => {
  if (!backdropPath) {
    return '/static/media/placeholder-backdrop.jpg'; // Fallback image
  }
  return `${TMDB_IMAGE_BASE_URL}/${size}${backdropPath}`;
};

/**
 * Generate profile image URL for actors/crew
 * @param {string} profilePath - The profile path from TMDB API
 * @param {string} size - Image size (w45, w185, h632, original)
 * @returns {string} Complete profile URL or fallback
 */
export const getProfileUrl = (profilePath, size = 'w185') => {
  if (!profilePath) {
    return '/static/media/placeholder-profile.jpg'; // Fallback image
  }
  return `${TMDB_IMAGE_BASE_URL}/${size}${profilePath}`;
};

/**
 * Get optimized poster URL based on container size
 * @param {string} posterPath - The poster path from TMDB API
 * @param {string} containerType - 'thumbnail', 'card', 'hero', 'full'
 * @returns {string} Optimized poster URL
 */
export const getOptimizedPosterUrl = (posterPath, containerType = 'card') => {
  const sizeMap = {
    thumbnail: 'w92',
    card: 'w342',
    hero: 'w500',
    full: 'w780'
  };
  
  return getPosterUrl(posterPath, sizeMap[containerType] || 'w342');
};

/**
 * Generate multiple poster URLs for responsive images
 * @param {string} posterPath - The poster path from TMDB API
 * @returns {object} Object with different sized URLs
 */
export const getResponsivePosterUrls = (posterPath) => {
  if (!posterPath) {
    return {
      thumbnail: '/static/media/placeholder-poster.jpg',
      small: '/static/media/placeholder-poster.jpg',
      medium: '/static/media/placeholder-poster.jpg',
      large: '/static/media/placeholder-poster.jpg',
      original: '/static/media/placeholder-poster.jpg'
    };
  }

  return {
    thumbnail: `${TMDB_IMAGE_BASE_URL}/w92${posterPath}`,
    small: `${TMDB_IMAGE_BASE_URL}/w154${posterPath}`,
    medium: `${TMDB_IMAGE_BASE_URL}/w342${posterPath}`,
    large: `${TMDB_IMAGE_BASE_URL}/w500${posterPath}`,
    original: `${TMDB_IMAGE_BASE_URL}/original${posterPath}`
  };
};

/**
 * Generate poster URL by movie ID (requires movie data)
 * @param {number} movieId - The TMDB movie ID
 * @param {object} movieData - Complete movie data object
 * @param {string} size - Image size
 * @returns {string} Complete poster URL
 */
export const getPosterUrlById = (movieId, movieData, size = 'w500') => {
  if (!movieData || !movieData.poster_path) {
    console.warn(`No poster data found for movie ID: ${movieId}`);
    return '/static/media/placeholder-poster.jpg';
  }
  
  return getPosterUrl(movieData.poster_path, size);
};

/**
 * Create a movie object with all poster URLs
 * @param {object} movieData - Raw movie data from TMDB API
 * @returns {object} Enhanced movie object with all poster URLs
 */
export const enhanceMovieWithPosters = (movieData) => {
  if (!movieData) return null;

  const posterUrls = getResponsivePosterUrls(movieData.poster_path);
  
  return {
    ...movieData,
    // Individual poster URLs
    poster_url: posterUrls.large, // Main poster URL (w500)
    poster_url_thumbnail: posterUrls.thumbnail,
    poster_url_small: posterUrls.small,
    poster_url_medium: posterUrls.medium,
    poster_url_large: posterUrls.large,
    poster_url_original: posterUrls.original,
    
    // Backdrop URLs
    backdrop_url: getBackdropUrl(movieData.backdrop_path, 'w1280'),
    backdrop_url_small: getBackdropUrl(movieData.backdrop_path, 'w780'),
    backdrop_url_original: getBackdropUrl(movieData.backdrop_path, 'original'),
    
    // All responsive URLs
    poster_urls: posterUrls
  };
};

/**
 * Get poster URL with fallback chain
 * @param {object} movieData - Movie data object
 * @param {string} size - Preferred size
 * @param {array} fallbackSizes - Array of fallback sizes
 * @returns {string} Poster URL with fallbacks
 */
export const getPosterUrlWithFallback = (movieData, size = 'w500', fallbackSizes = ['w342', 'w154', 'w92']) => {
  if (!movieData || !movieData.poster_path) {
    return '/static/media/placeholder-poster.jpg';
  }

  // Try preferred size first
  const preferredUrl = getPosterUrl(movieData.poster_path, size);
  
  // If you want to implement fallback checking (requires image loading test)
  // For now, just return the preferred URL
  return preferredUrl;
};

export default {
  getPosterUrl,
  getBackdropUrl,
  getProfileUrl,
  getOptimizedPosterUrl,
  getResponsivePosterUrls,
  getPosterUrlById,
  enhanceMovieWithPosters,
  getPosterUrlWithFallback
};
